import { MIN_STAKE, type DeathMessage } from '@star-snake/shared'
import {
  NeonColors,
  neonButton,
  neonText,
  SCREEN_BASE_STYLE,
  fadeTransition,
  cosmicBackground,
  isTouchUiMode,
  FONTS,
} from '../neon-theme'
import { getPlayerColorKey, generateSnakeColors } from '../../game/snakeColors'
import { PaymentClient } from '../../net/PaymentClient'
import { NetworkManager } from '../../network/NetworkManager'
import { getStartActionState, type StartActionKind } from './startActionState'
import {
  drawResultSnake,
  drawTrashGoal,
  formatSparkStarLine,
  getResultSnakeSpacing,
  getResultSnakeVisualSegments,
} from './resultSnakeArt'
import { getAppViewport, mountToAppShell } from '../appShell'

const RESULT_BASE_LENGTH = 10
const MAX_CANVAS_DPR = 2

export interface DeathScreenOptions {
  balance: number
  freeGamesLeft: number
  lastStake?: number
}

function setupHiDpiCanvas(canvas: HTMLCanvasElement, width: number, height: number): CanvasRenderingContext2D | null {
  const dpr = Math.min(window.devicePixelRatio || 1, isTouchUiMode() ? 1 : MAX_CANVAS_DPR)
  canvas.width = Math.floor(width * dpr)
  canvas.height = Math.floor(height * dpr)
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  return ctx
}

export class DeathScreen {
  private container: HTMLDivElement
  private onRespawn: ((stake: number) => void) | null = null
  private onExit: (() => void) | null = null
  private animationTimers: number[] = []
  private animationFrameId: number | null = null
  private hideTimerId: number | null = null

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'death-screen'
    this.container.style.cssText = `
      ${SCREEN_BASE_STYLE}
      background: ${NeonColors.bgDark};
      padding: 20px; box-sizing: border-box; overflow-y: auto;
      opacity: 0;
      ${fadeTransition(300)}
    `
  }

  show(
    data: DeathMessage,
    onRespawn: (stake: number) => void,
    onExit: () => void,
    options?: DeathScreenOptions,
  ): void {
    this.resetTimers()
    this.onRespawn = onRespawn
    this.onExit = onExit
    const lightweightUi = isTouchUiMode()

    const balance = options?.balance ?? 0
    const freeGamesLeft = options?.freeGamesLeft ?? 0
    const lastStake = options?.lastStake ?? MIN_STAKE
    const startAction = getStartActionState(balance, freeGamesLeft)
    const canAfford = startAction.kind !== 'deposit'
    const snakeLength = RESULT_BASE_LENGTH + Math.max(0, Math.floor(data.sparksDropped))
    const lostSparks = Math.max(0, Math.floor(data.sparksDropped))

    this.container.innerHTML = ''
    if (!lightweightUi) {
      this.container.insertAdjacentHTML('afterbegin', cosmicBackground())
    }

    const styleEl = document.createElement('style')
    styleEl.textContent = `
      @keyframes deathPulse {
        0%, 100% { text-shadow: 0 0 20px ${NeonColors.dangerGlow}, 0 0 48px ${NeonColors.dangerGlow}; }
        50% { text-shadow: 0 0 34px ${NeonColors.dangerGlow}, 0 0 90px ${NeonColors.dangerGlow}; }
      }
      @keyframes deathFadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .death-fade-in {
        animation: deathFadeIn 0.45s ease-out forwards;
        opacity: 0;
      }
    `
    this.container.appendChild(styleEl)

    const content = document.createElement('div')
    content.style.cssText = 'z-index: 30; display: flex; flex-direction: column; align-items: center;'

    const title = document.createElement('h1')
    title.style.cssText = `
      font-size: clamp(2.6rem, 10vw, 3.2rem);
      font-weight: 900;
      letter-spacing: 0.22em;
      margin: 0 0 0.6rem 0;
      ${neonText(NeonColors.danger, NeonColors.dangerGlow)}
      ${lightweightUi ? '' : 'animation: deathPulse 1.4s ease-in-out infinite;'}
      font-family: ${FONTS.title};
    `
    title.textContent = 'YOU DIED'
    content.appendChild(title)

    const reasonEl = document.createElement('p')
    reasonEl.style.cssText = `
      color: ${NeonColors.textMuted};
      font-size: 1rem;
      margin: 0 0 1.4rem 0;
      text-align: center;
      max-width: 320px;
    `
    reasonEl.textContent = this.getReasonText(data)
    content.appendChild(reasonEl)

    const canvasWidth = Math.min(360, getAppViewport().width - 36)
    const canvasHeight = 132
    const visualSegments = getResultSnakeVisualSegments(snakeLength, canvasWidth)
    const animationDurationMs = Math.min(3600, Math.max(1700, visualSegments * 44))
    const animCanvas = document.createElement('canvas')
    const ctx = setupHiDpiCanvas(animCanvas, canvasWidth, canvasHeight)
    animCanvas.style.cssText = `display: block; margin: 0 auto 14px auto;`
    content.appendChild(animCanvas)

    const summaryLine = document.createElement('div')
    summaryLine.style.cssText = `
      min-height: 44px;
      font-size: 1rem;
      font-weight: 700;
      text-align: center;
      margin-bottom: 22px;
      ${neonText(NeonColors.gold, NeonColors.goldGlow)}
    `
    summaryLine.textContent = formatSparkStarLine(0, 'spark lost', 'sparks lost')
    content.appendChild(summaryLine)

    const buttonsArea = document.createElement('div')
    buttonsArea.style.cssText = 'display: flex; flex-direction: column; align-items: center; width: 100%; opacity: 0; pointer-events: none;'

    if (canAfford) {
      this.buildRespawnSection(buttonsArea, lastStake, startAction.kind, startAction.badgeText, startAction.helperText)
    } else {
      const noFundsEl = document.createElement('div')
      noFundsEl.style.cssText = 'text-align: center; margin-bottom: 1.5rem;'

      const noFundsText = document.createElement('p')
      noFundsText.style.cssText = `font-size: 0.95rem; margin-bottom: 12px; ${neonText(NeonColors.danger, NeonColors.dangerGlow)}`
      noFundsText.textContent = 'Insufficient balance to respawn'
      noFundsEl.appendChild(noFundsText)

      const depositBtn = document.createElement('button')
      depositBtn.style.cssText = `${neonButton(NeonColors.gold, NeonColors.goldGlow)}`
      depositBtn.innerHTML = `
        <span style="display: flex; align-items: center; gap: 12px;">
          <span>DEPOSIT START</span>
          <span style="background: rgba(255,255,255,0.22); color: #0f172a; border-radius: 999px; padding: 4px 10px; font-size: 0.78rem; font-weight: 900; letter-spacing: 0.06em;">
            ${startAction.badgeText}
          </span>
        </span>
      `
      depositBtn.addEventListener('click', async () => {
        depositBtn.disabled = true
        depositBtn.style.opacity = '0.75'

        try {
          const network = NetworkManager.getInstance()
          const token = network.getToken()
          if (!token) throw new Error('Not authenticated')
          const paid = await PaymentClient.deposit(MIN_STAKE, token)
          await network.fetchBalance()
          if (paid) {
            this.show(data, onRespawn, onExit, {
              balance: network.balance,
              freeGamesLeft: network.freeGamesLeft,
              lastStake,
            })
            return
          }
        } catch {
          // Keep the screen visible and let the player retry.
        }

        depositBtn.disabled = false
        depositBtn.style.opacity = '1'
      })
      noFundsEl.appendChild(depositBtn)

      if (startAction.helperText) {
        const helperEl = document.createElement('div')
        helperEl.style.cssText = `margin-top: 12px; font-size: 0.85rem; color: ${NeonColors.textMuted};`
        helperEl.textContent = startAction.helperText
        noFundsEl.appendChild(helperEl)
      }

      buttonsArea.appendChild(noFundsEl)
    }

    if (startAction.showBalance) {
      const balanceEl = document.createElement('div')
      balanceEl.style.cssText = `
        font-size: 0.85rem; margin-bottom: 16px;
        ${neonText(NeonColors.gold, NeonColors.goldGlow)}
        background: rgba(255,215,0,0.05); border: 1px solid rgba(255,215,0,0.15);
        border-radius: 12px; padding: 8px 18px;
      `
      balanceEl.textContent = `Balance: ${balance} star${balance !== 1 ? 's' : ''}`
      buttonsArea.appendChild(balanceEl)
    }

    const exitBtn = document.createElement('button')
    exitBtn.style.cssText = `
      padding: 12px 24px; font-size: 0.9rem;
      background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
      border-radius: 12px; color: ${NeonColors.textMuted}; cursor: pointer;
      transition: color 0.15s, border-color 0.15s, background 0.15s;
      font-family: ${FONTS.body};
    `
    exitBtn.textContent = 'Exit to Menu'
    exitBtn.addEventListener('mouseenter', () => {
      exitBtn.style.color = NeonColors.textBright
      exitBtn.style.borderColor = 'rgba(255,255,255,0.2)'
      exitBtn.style.background = 'rgba(255,255,255,0.1)'
    })
    exitBtn.addEventListener('mouseleave', () => {
      exitBtn.style.color = NeonColors.textMuted
      exitBtn.style.borderColor = 'rgba(255,255,255,0.1)'
      exitBtn.style.background = 'rgba(255,255,255,0.05)'
    })
    exitBtn.addEventListener('click', () => {
      this.onExit?.()
      this.hide()
    })
    buttonsArea.appendChild(exitBtn)

    content.appendChild(buttonsArea)
    this.container.appendChild(content)
    mountToAppShell(this.container)
    requestAnimationFrame(() => { this.container.style.opacity = '1' })

    if (ctx) {
      this.startDeathAnimation(ctx, canvasWidth, canvasHeight, snakeLength, lostSparks, summaryLine)
    }

    this.scheduleTimer(() => {
      buttonsArea.style.opacity = '1'
      buttonsArea.className = 'death-fade-in'
      buttonsArea.style.pointerEvents = 'auto'
    }, animationDurationMs + 220)
  }

  hide(): void {
    this.resetTimers()
    for (const el of this.container.querySelectorAll('.cosmic-nebula, .cosmic-star-field')) {
      (el as HTMLElement).style.display = 'none'
    }
    this.container.style.opacity = '0'
    this.hideTimerId = window.setTimeout(() => {
      this.container.remove()
      this.onRespawn = null
      this.onExit = null
      this.hideTimerId = null
    }, 300)
  }

  dispose(): void {
    this.resetTimers()
    this.container.remove()
    this.onRespawn = null
    this.onExit = null
  }

  private startDeathAnimation(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    snakeLength: number,
    lostSparks: number,
    summaryLine: HTMLDivElement,
  ): void {
    const lightweightUi = isTouchUiMode()
    const frameStepMs = lightweightUi ? 33 : 0
    const colorSeed = parseInt(localStorage.getItem('star-snake-color-seed') || '0', 10) || 0
    const colors = generateSnakeColors(getPlayerColorKey(colorSeed))
    const trashSize = 62
    const trashInset = Math.max(86, trashSize * 1.35)
    const trashX = width - trashInset
    const centerY = height * 0.5
    const visualSegments = getResultSnakeVisualSegments(snakeLength, width)
    const spacing = getResultSnakeSpacing(width, visualSegments)
    const catchX = trashX - 18
    const startHeadX = -64
    const endHeadX = catchX + spacing * (visualSegments + 1)
    const startTime = performance.now()
    const totalDuration = Math.min(3600, Math.max(1700, visualSegments * 44))
    let displayedSparks = -1
    let displayedSegments = -1
    let lastDrawAt = 0
    const hitTimes: number[] = []

    const tick = (now: number): void => {
      if (frameStepMs > 0 && lastDrawAt > 0 && now - lastDrawAt < frameStepMs) {
        this.animationFrameId = requestAnimationFrame(tick)
        return
      }
      lastDrawAt = now
      const elapsed = now - startTime
      const progress = Math.min(1, elapsed / totalDuration)
      const eased = 1 - Math.pow(1 - progress, 2.35)
      const headX = startHeadX + (endHeadX - startHeadX) * eased
      const consumedSegments = Math.max(
        0,
        Math.min(visualSegments, Math.floor((headX - catchX) / spacing) + 1),
      )
      const swallowed = Math.round(lostSparks * (consumedSegments / visualSegments))

      if (consumedSegments > displayedSegments) {
        for (let seg = displayedSegments + 1; seg <= consumedSegments; seg++) {
          if (seg > 0) hitTimes.push(now)
        }
        displayedSegments = consumedSegments
      }

      for (let i = hitTimes.length - 1; i >= 0; i--) {
        if (now - hitTimes[i] > 180) hitTimes.splice(i, 1)
      }
      const pulse = Math.min(1.35, hitTimes.reduce((sum, hitTime) => sum + Math.max(0, 1 - (now - hitTime) / 180), 0))

      ctx.clearRect(0, 0, width, height)
      drawTrashGoal(ctx, trashX, centerY, trashSize, pulse)
      drawResultSnake(ctx, {
        canvasWidth: width,
        canvasHeight: height,
        headX,
        centerY,
        length: snakeLength,
        timeMs: now,
        colors,
        fadeToWhite: Math.max(0, (headX - (catchX - 36)) / 76),
        alpha: 1,
        consumedSegments,
      })

      if (swallowed !== displayedSparks) {
        displayedSparks = swallowed
        summaryLine.textContent = formatSparkStarLine(swallowed, 'spark lost', 'sparks lost')
      }

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(tick)
      } else {
        summaryLine.textContent = formatSparkStarLine(lostSparks, 'spark lost', 'sparks lost')
        this.animationFrameId = null
      }
    }

    this.animationFrameId = requestAnimationFrame(tick)
  }

  private scheduleTimer(fn: () => void, delayMs: number): void {
    const id = window.setTimeout(fn, delayMs)
    this.animationTimers.push(id)
  }

  private resetTimers(): void {
    for (const id of this.animationTimers) clearTimeout(id)
    this.animationTimers = []
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
    if (this.hideTimerId !== null) {
      clearTimeout(this.hideTimerId)
      this.hideTimerId = null
    }
  }

  private getReasonText(data: DeathMessage): string {
    if (data.reason === 'RED_ZONE') return 'Consumed by the Red Zone'
    if (data.reason === 'HEAD_ON') {
      return data.killedByName ? `Head-on with ${data.killedByName}` : 'Head-on collision'
    }
    if (data.reason === 'SESSION_END') return 'The room ended before you could evacuate'
    if (data.killedByName) return `Killed by ${data.killedByName}`
    return 'You died'
  }

  private buildRespawnSection(
    parent: HTMLDivElement,
    _lastStake: number,
    _startKind: StartActionKind,
    badgeText: string,
    helperText: string | null,
  ): void {
    const respawnBtn = document.createElement('button')
    respawnBtn.style.cssText = `
      ${neonButton(NeonColors.danger, NeonColors.dangerGlow)}
      margin-bottom: 16px; font-size: 1.1rem;
      color: #fff;
    `
    respawnBtn.innerHTML = `
      <span style="display: flex; align-items: center; gap: 12px;">
        <span>RESPAWN</span>
        <span style="background: rgba(255,255,255,0.22); color: #fff; border-radius: 999px; padding: 4px 10px; font-size: 0.78rem; font-weight: 900; letter-spacing: 0.06em;">
          ${badgeText}
        </span>
      </span>
    `
    respawnBtn.addEventListener('mouseenter', () => {
      respawnBtn.style.transform = 'scale(1.03)'
      respawnBtn.style.boxShadow = `0 0 30px ${NeonColors.dangerGlow}, 0 0 60px ${NeonColors.dangerGlow}`
    })
    respawnBtn.addEventListener('mouseleave', () => {
      respawnBtn.style.transform = 'scale(1)'
      respawnBtn.style.boxShadow = `0 0 20px ${NeonColors.dangerGlow}, 0 0 40px ${NeonColors.dangerGlow}`
    })
    respawnBtn.addEventListener('click', () => {
      this.onRespawn?.(MIN_STAKE)
      this.hide()
    })
    parent.appendChild(respawnBtn)

    if (helperText) {
      const helperEl = document.createElement('div')
      helperEl.style.cssText = `margin-top: -6px; margin-bottom: 16px; font-size: 0.85rem; color: ${NeonColors.info};`
      helperEl.textContent = helperText
      parent.appendChild(helperEl)
    }
  }
}
